import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronRight, Copy, GripVertical, Loader2, Play } from 'lucide-react';
import { NODE_CATALOG } from 'features/agents/WorkflowEditor/nodeCatalog';
import { nodeExprBase } from 'features/agents/WorkflowEditor/workflowGraph';
// The same "Formatted" renderer the run popup uses: markdown for text, titled
// turns for chat arrays, tables for tabular data, a readable tree for the rest.
import { RichValueView } from 'features/agent-analytics/components/runCells';
import { EXPR_MIME, copyText, cx, neutralBtnCls } from 'features/agents/WorkflowEditor/fields/inputs';

/**
 * The Input and Output panes of a node. Input shows what upstream steps
 * produced, as a field tree you can drag from or copy a `{{ }}` reference of;
 * Output shows what this step returned (or the error it raised).
 */

const MODE_LABEL = { formatted: 'Formatted', schema: 'Schema', json: 'JSON' };

function ModeSwitch({ modes, value, onChange, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-[color:var(--border-default)] p-0.5">
      {modes.map((m) => (
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={value === m}
          onClick={() => onChange(m)}
          className={cx(
            'h-5 rounded px-2 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]',
            value === m
              ? 'bg-[color:var(--surface-hover)] text-[color:var(--text-primary)]'
              : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]',
          )}
        >
          {MODE_LABEL[m] || m}
        </button>
      ))}
    </div>
  );
}

function PaneHeader({ title, children }) {
  return (
    <div className="flex h-10 shrink-0 items-center gap-2 border-b border-[color:var(--border-default)] px-3">
      <span className="flex-1 text-[12px] font-semibold text-[color:var(--text-primary)]">{title}</span>
      {children}
    </div>
  );
}

function Empty({ title, hint }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 px-4 py-8 text-center">
      <span className="text-[12px] text-[color:var(--text-muted)]">{title}</span>
      {hint ? <span className="text-[11px] text-[color:var(--text-disabled)]">{hint}</span> : null}
    </div>
  );
}

const errorBoxCls = 'whitespace-pre-wrap break-words rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-2.5 font-mono text-[11px] text-danger';
const jsonBoxCls = 'whitespace-pre-wrap break-words rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-base)] p-2.5 font-mono text-[11px] leading-relaxed text-[color:var(--text-primary)]';

function stringify(v) {
  try { return JSON.stringify(v, null, 2); } catch (_) { return String(v); }
}

export function InputPane({ upstreamNodes, testData, draggable, executing, onExecutePrevious }) {
  const [view, setView] = useState('schema');
  const [openSections, setOpenSections] = useState(() => new Set());

  const sections = useMemo(() => upstreamNodes.map((n) => {
    const kind = n.kind || n.data?.kind;
    const name = n.name || n.data?.name || NODE_CATALOG[kind]?.displayName || kind || 'Node';
    const step = testData[n.id];
    // Each section shows that node's output only. Falling back to `input` would
    // wrongly show the predecessor's data under a failed step.
    const value = step?.status === 'succeeded' ? (step.output ?? null) : null;
    return { id: n.id, name, kind, step, value };
  }), [upstreamNodes, testData]);

  const hasAnyData = sections.some((s) => s.value != null);

  useEffect(() => {
    setOpenSections(new Set(upstreamNodes.map((n) => n.id)));
  }, [upstreamNodes]);

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--surface-sunken)]">
      <PaneHeader title="Input">
        {hasAnyData ? <ModeSwitch modes={['schema', 'json']} value={view} onChange={setView} label="Input view" /> : null}
      </PaneHeader>

      <div className="min-h-0 flex-1 overflow-auto p-2">
        {sections.length === 0 ? (
          <Empty title="No upstream steps" hint="Connect a trigger or another step before this one." />
        ) : (
          <>
            {onExecutePrevious ? (
              <button type="button" onClick={onExecutePrevious} disabled={executing} className={cx(neutralBtnCls, 'mb-2 w-full bg-[color:var(--surface-base)]')}>
                {executing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Run previous steps
              </button>
            ) : null}

            {!hasAnyData ? (
              <p className="mb-2 px-1 text-center text-[11px] leading-relaxed text-[color:var(--text-muted)]">
                Run the steps before this one to load field names and values.
              </p>
            ) : null}

            {draggable && hasAnyData && view === 'schema' ? (
              <p className="mb-2 flex items-center gap-1 px-1 text-[11px] text-[color:var(--text-muted)]">
                <Copy className="h-3 w-3" aria-hidden /> Drag a field into a setting, or copy its reference.
              </p>
            ) : null}

            <div className="space-y-1.5">
              {sections.map((s) => {
                const spec = NODE_CATALOG[s.kind];
                const open = openSections.has(s.id);
                const expr = `${nodeExprBase(s.name)}.json`;
                return (
                  <div key={s.id} className="overflow-hidden rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-base)]">
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={open}
                      onClick={() => toggleSection(s.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(s.id); } }}
                      className="group flex h-8 w-full cursor-pointer items-center gap-1.5 px-2.5 text-left transition-colors hover:bg-[color:var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]"
                    >
                      <ChevronRight className={cx('h-3 w-3 shrink-0 text-[color:var(--text-muted)] transition-transform duration-150', open && 'rotate-90')} />
                      <span className="flex-1 truncate text-[12px] font-medium text-[color:var(--text-primary)]">{s.name}</span>
                      {spec?.displayName && spec.displayName !== s.name ? (
                        <span className="max-w-[90px] truncate text-[11px] text-[color:var(--text-disabled)]">{spec.displayName}</span>
                      ) : null}
                      {s.step?.status === 'failed' ? <span className="shrink-0 text-[11px] font-medium text-danger">Failed</span> : null}
                      <CopyRef token={`{{ ${expr} }}`} title="Copy a reference to this step's whole output" />
                    </div>

                    {open ? (
                      <div className="border-t border-[color:var(--border-subtle)] p-1.5">
                        {!s.step ? (
                          <p className="px-1 py-2 text-[11px] text-[color:var(--text-muted)]">Not run yet. Run the previous steps above.</p>
                        ) : s.step.status === 'failed' ? (
                          <pre className={errorBoxCls}>{s.step.error?.message || 'This step failed.'}</pre>
                        ) : s.value == null ? (
                          <p className="px-1 py-2 text-[11px] text-[color:var(--text-muted)]">This step ran and returned nothing.</p>
                        ) : view === 'json' ? (
                          <pre className={jsonBoxCls}>{stringify(s.value)}</pre>
                        ) : (
                          <SchemaNode keyName={null} value={s.value} expr={expr} draggable={!!draggable} depth={0} isRoot />
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function IOPane({ title, value, error, draggable, exprBase = '$json', emptyHint }) {
  // 'formatted' reads best (markdown, titled turns, tables); 'schema' is the
  // draggable field explorer; 'json' is raw. Panes meant for reading default
  // to Formatted, the drag-into-expressions explorer defaults to Schema.
  const modes = draggable ? ['formatted', 'schema', 'json'] : ['formatted', 'json'];
  const [view, setView] = useState(draggable ? 'schema' : 'formatted');
  const json = useMemo(() => {
    if (error) return stringify(error);
    if (value == null) return null;
    return stringify(value);
  }, [value, error]);
  const hasData = value != null && !error;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--surface-sunken)]">
      <PaneHeader title={title}>
        {hasData ? <ModeSwitch modes={modes} value={view} onChange={setView} label={`${title} view`} /> : null}
      </PaneHeader>

      <div className="min-h-0 flex-1 overflow-auto p-2">
        {error ? (
          <pre className={errorBoxCls}>{json}</pre>
        ) : !hasData ? (
          <Empty title="No data yet" hint={emptyHint || `Run the agent to see its ${title.toLowerCase()}.`} />
        ) : view === 'json' ? (
          <pre className={jsonBoxCls}>{json}</pre>
        ) : view === 'schema' ? (
          <>
            {draggable ? (
              <p className="mb-2 flex items-center gap-1 px-1 text-[11px] text-[color:var(--text-muted)]">
                <GripVertical className="h-3 w-3" aria-hidden /> Drag any field into a setting.
              </p>
            ) : null}
            <SchemaNode keyName={null} value={value} expr={exprBase} draggable={!!draggable} depth={0} isRoot />
          </>
        ) : (
          <div className="min-w-0 text-[12px] text-[color:var(--text-primary)]">
            <RichValueView value={value} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Copy a `{{ reference }}` to the clipboard. The Input tab and the settings
 * tab are never on screen together, so a drag cannot cross between them; a
 * copy can.
 */
function CopyRef({ token, title }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        copyText(token).catch(() => {}).finally(() => { setDone(true); setTimeout(() => setDone(false), 1200); });
      }}
      title={title || `Copy ${token}`}
      aria-label={title || `Copy ${token}`}
      className={cx(
        'inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[11px] transition-opacity hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] focus-visible:opacity-100',
        done ? 'text-[color:var(--feedback-success)] opacity-100' : 'text-[color:var(--text-disabled)] opacity-0 group-hover:opacity-100',
      )}
    >
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {done ? 'Copied' : null}
    </button>
  );
}

const IDENT_RE = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
function childExpr(parentExpr, key, isIndex) {
  if (isIndex) return `${parentExpr}[${key}]`;
  return IDENT_RE.test(key) ? `${parentExpr}.${key}` : `${parentExpr}[${JSON.stringify(key)}]`;
}

function previewValue(v) {
  if (v === null) return 'null';
  if (typeof v === 'string') return v.length > 36 ? `"${v.slice(0, 36)}…"` : `"${v}"`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === 'object') return `{${Object.keys(v).length}}`;
  return String(v);
}

function SchemaNode({ keyName, value, expr, draggable, depth, isRoot }) {
  const isObj = value !== null && typeof value === 'object' && !Array.isArray(value);
  const isArr = Array.isArray(value);
  const isBranch = isObj || isArr;
  const [open, setOpen] = useState(depth < 2);
  const token = `{{ ${expr} }}`;

  const dragProps = draggable ? {
    draggable: true,
    onDragStart: (e) => {
      e.stopPropagation();
      e.dataTransfer.setData(EXPR_MIME, token);
      e.dataTransfer.setData('text/plain', token);
      e.dataTransfer.effectAllowed = 'copy';
    },
  } : {};

  const entries = isObj ? Object.entries(value) : isArr ? value.map((v, i) => [String(i), v]) : [];
  const rowCls = cx(
    'group flex select-none items-center gap-1.5 rounded-md px-1.5 py-[5px] transition-colors',
    draggable && 'cursor-grab hover:bg-[color:var(--surface-hover)] active:cursor-grabbing',
  );

  if (keyName !== null && !isBranch) {
    return (
      <div className={cx(rowCls, 'gap-2 px-2')} {...dragProps} title={draggable ? `Drag to insert ${token}` : undefined}>
        <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[color:var(--text-primary)]">{keyName}</span>
        <span className="max-w-[100px] shrink-0 truncate font-mono text-[11px] text-[color:var(--text-muted)]">{previewValue(value)}</span>
        {draggable ? <CopyRef token={token} /> : null}
      </div>
    );
  }

  return (
    <div>
      {keyName !== null ? (
        <div className={rowCls} {...dragProps} title={draggable ? `Drag to insert ${token}` : undefined}>
          <button
            type="button"
            aria-label={open ? 'Collapse' : 'Expand'}
            aria-expanded={open}
            onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
            className="shrink-0 text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
          >
            <ChevronRight className={cx('h-3 w-3 transition-transform duration-150', open && 'rotate-90')} />
          </button>
          <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[color:var(--text-primary)]">{keyName}</span>
          <span className="shrink-0 font-mono text-[11px] text-[color:var(--text-muted)]">{previewValue(value)}</span>
          {draggable ? <CopyRef token={token} /> : null}
        </div>
      ) : null}
      {isBranch && open ? (
        <div className={isRoot ? '' : 'ml-[7px] border-l border-[color:var(--border-subtle)] pl-3'}>
          {entries.length === 0 ? (
            <div className="px-2 py-1 text-[11px] text-[color:var(--text-disabled)]">{isArr ? 'Empty list' : 'Empty object'}</div>
          ) : null}
          {entries.map(([k, v]) => (
            <SchemaNode key={k} keyName={k} value={v} expr={childExpr(expr, k, isArr)} draggable={draggable} depth={depth + 1} />
          ))}
        </div>
      ) : null}
      {isRoot && !isBranch ? (
        <div className="px-2 py-[5px] font-mono text-[12px] text-[color:var(--text-primary)]">{previewValue(value)}</div>
      ) : null}
    </div>
  );
}
